import {AssessmentDataCategory} from './assessment-data-category';
import {TylerMatTableDataSource} from './../../shared-components/angular-material/extended.class';
import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit} from '@angular/core';
import {Assessment} from './assessment.model';
import {AssessmentService} from './assessment.service';
import {Subscription} from 'rxjs';
import {StudentService} from '../../shared-components/student/student.service';
import {UserService} from '../../shared-components/user/user.service';
import {YearService} from '../../shared-components/year/year.service';
import {Student} from '../../shared-components/student/student.model';
import {SpinnerService} from '../../shared-components/spinner/spinner.service';
import { Router } from '@angular/router';

@Component({
	selector: 'assessment',
	templateUrl: './assessment.component.html',
    styleUrls: ['./assessment.component.css']
})
export class AssessmentComponent implements OnInit, OnDestroy {

	// Properties
	V10: boolean;
	tabs: Assessment[];
	assessments: Assessment[];
	subscriptions: Subscription[] = [];
	tabNames: string[];
	tableDataV9: TylerMatTableDataSource[] = [];
	openDetailRows: Array<AssessmentDataCategory> = new Array<AssessmentDataCategory>();
	tableDataV10: TylerMatTableDataSource[] = [];
	dataCategoriesV10: AssessmentDataCategory[];
	selectionIndex = 0;
	toggleAllRows: EventEmitter<boolean> = new EventEmitter<boolean>();
    selectedRow: AssessmentDataCategory;
    assessmentAndTabSubscriptions: Subscription[] = [];
    selectedStudent: Student;
    selectedTabName = '';
    previousTabSelection = '';
    processingTabs = false;
	triggerChanges = new EventEmitter<any>();

	// Class Functions
	constructor (
		private assessmentService: AssessmentService,
		private studentService: StudentService,
		private userService: UserService,
		private yearService: YearService,
		private changeDetector: ChangeDetectorRef,
        private spinnerService: SpinnerService,
        private router: Router
	) { }

	get filtered(): boolean {
		if (this.V10 && this.tableDataV10[this.selectionIndex]) {
			return this.tableDataV10[this.selectionIndex].isFiltered();
		} else if (this.tableDataV9[this.selectionIndex]) {
			let filtered = this.tableDataV9[this.selectionIndex].isFiltered();
			for (let i = 0; i < this.tableDataV9[this.selectionIndex].data.length; i++) {
				const subTable = this.tableDataV9[this.selectionIndex].data[i];
				if (subTable instanceof AssessmentDataCategory && subTable.subTableData.isFiltered()) {
					filtered = true;
					break;
				}
			}
			return filtered;
		}
		return false;
	}

	// Event Functions
	ngOnInit() {
		// POST: subscribing to the necessary services
		this.V10 = this.userService.currentUser.V10;
		this.subscriptions.push(
			this.studentService.selectedStudentStream$.subscribe(() => {
                if (this.selectedStudent != this.studentService.selectedStudent) {
                	this.previousTabSelection = this.selectedTabName;
                    this.selectedStudent = this.studentService.selectedStudent;
                    this.getAssessmentsAndTabs();
                }
			}),
			this.yearService.observableStudent360YearStream$.subscribe(() => {
				this.previousTabSelection = this.selectedTabName;
				this.getAssessmentsAndTabs();
			}),
			this.toggleAllRows.subscribe(toggle => {
                if (!this.V10) {
                    this.tableDataV9[this.selectionIndex].expandAll(toggle);
                    this.triggerChanges.emit();
                }
            })
        );
    }

    ngOnDestroy() {
		// POST: ends all subscriptions
		this.subscriptions.forEach(subscription => {
			subscription.unsubscribe();
        });
        this.assessmentAndTabSubscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
	}

	selectionChange(selectionIndex: number) {
		// PRE: the tab selection changes and this is passed the index of the tab
		// POST: sets the current tab
        this.spinnerService.showSpinner = true;
        setTimeout(() => {
            this.selectionIndex = selectionIndex;
            this.selectedTabName = this.tabNames[selectionIndex];
            setTimeout(() => {
                this.spinnerService.showSpinner = false;
            }, 100);
        }, 500);
	}

	clearFilter() {
		// POST: clears the filters from the current tab's table
		if (this.V10 && this.tableDataV10[this.selectionIndex]) {
			this.tableDataV10[this.selectionIndex].removeAllFilters();
		} else if (this.tableDataV9[this.selectionIndex]) {
			this.tableDataV9[this.selectionIndex].removeAllFilters();
			for (let i = 0; i < this.tableDataV9[this.selectionIndex].data.length; i++) {
				const subTable = this.tableDataV9[this.selectionIndex].data[i];
				if (subTable instanceof AssessmentDataCategory) {
					subTable.subTableData.removeAllFilters();
				}
			}
		}
	}

	// Methods
	getAssessmentsAndTabs() {
        // POST: obtains the assessments and tabs, setting tab names and assessments

		// Preparing for processing
		this.processingTabs = true;
        this.assessmentAndTabSubscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
        this.assessmentAndTabSubscriptions = [];
        let yearId = (!this.regEnrollMode) ? this.studentService.studentEnrollment.SiteYearId : this.yearService.defaultYear.Id;

        // Obtaining data
		this.assessmentAndTabSubscriptions.push(

			// Obtaining tabs
			this.assessmentService.getStudentAssessmentTabs(yearId).subscribe(tabs => {
				this.tabNames = [];
				tabs.forEach(tab => {
					this.tabNames.push(tab.DESCRIPTION);
				});
				this.tabs = tabs;

				// Changing to the first tab
				this.selectionIndex = 0;
				this.selectedTabName = this.tabNames[0];

				// Obtaining assessments
				this.assessmentAndTabSubscriptions.push(
					this.assessmentService.getStudentAssessments(tabs).subscribe(assessments => {
						this.assessments = assessments;
						this.arrangeTabs();
						this.processingTabs = false;
					})
				);
			})
		);
	}

	arrangeTabs() {
        // PRE: the tabs and data are obtained
        // POST: arranges the data for display and sets the initial grid size
		if (this.V10) {
			this.arrangeTabsV10();
		} else {
			this.arrangeTabsV9();
        }

		// If this is V9 and the first tab, we expand all
		setTimeout(() => {
			if (!this.V10 && this.selectionIndex == 0) {
				this.toggleAllRows.emit(true);
			}
		}, 10);

		// Running change detection manually because we made a ton of changes
		this.changeDetector.detectChanges();
	}

	arrangeTabsV9 () {
		// PRE: Your user MUST be in V9, or this doesn't make sense
		// POST: finds the tabs and arranges the data for each tab
		this.tableDataV9 = [];
		const dataArrays: Array<Array<AssessmentDataCategory>> = new Array<Array<AssessmentDataCategory>>();

		// Creating a data category for each tab
		for (let i = 0; i < this.tabNames.length; i++) {
			dataArrays.push(new Array<AssessmentDataCategory>());
		}

		for (let i = 0; i < this.assessments.length; i++) {

			// Only operating on the assessment if we should display this
			if (this.assessments[i].Display) {

				// Determining where to place this data
				const tableIndex = this.tabNames.indexOf(this.assessments[i].FULLTITLE);
				const dataCategories = dataArrays[tableIndex];
				let categoryExists = false;
				let categoryIndex = -1;
				for (let j = 0; j < dataCategories.length; j++) {
					if (categoryIndex < 0 &&
						dataCategories[j].category == this.assessments[i].DESCRIPTION &&
						dataCategories[j].testName == this.assessments[i].FULLTITLE) {
						categoryExists = true;
						categoryIndex = j;
					}
				}
				const bestOfTableIndex = this.tabNames.indexOf('Best Of');
				const bestOfCategories = bestOfTableIndex >= 0 ? dataArrays[bestOfTableIndex] : null;
				let bestOfCategoryExists = false;
				let bestOfIndex = -1;
				if (bestOfCategories && this.assessments[i].BESTOF) {
					for (let j = 0; j < bestOfCategories.length; j++) {
						if (bestOfIndex < 0 &&
							bestOfCategories[j].category == this.assessments[i].DESCRIPTION &&
							bestOfCategories[j].testName == this.assessments[i].FULLTITLE) {
							bestOfCategoryExists = true;
							bestOfIndex = j;
						}
					}
				}

				// If not place was found, creating a new category for it
				if (!categoryExists) {
					dataCategories.push(new AssessmentDataCategory());
                    categoryIndex = dataCategories.length - 1;
                    let assessment = this.assessments[i];
					dataCategories[categoryIndex].category = assessment.DESCRIPTION;
					dataCategories[categoryIndex].testName = assessment.FULLTITLE;
                    dataCategories[categoryIndex].fullTitle = assessment.DESCRIPTION;
                    let dataFields = assessment.AssessmentDataField;
                    if (dataFields != null && dataFields.length > 0)
                        dataCategories[categoryIndex].instanceId = dataFields[0].TestInstanceId;
				}
				if (this.assessments[i].BESTOF && !bestOfCategoryExists) {
					bestOfCategories.push(new AssessmentDataCategory());
					bestOfIndex = bestOfCategories.length - 1;
                    let assessment = this.assessments[i];
					bestOfCategories[bestOfIndex].category = this.assessments[i].DESCRIPTION;
					bestOfCategories[bestOfIndex].testName = this.assessments[i].FULLTITLE;
					bestOfCategories[bestOfIndex].fullTitle = this.assessments[i].FULLTITLE + ' - ' + this.assessments[i].DESCRIPTION;
                    let dataFields = assessment.AssessmentDataField;
                    if (dataFields != null && dataFields.length > 0)
                        bestOfCategories[categoryIndex].instanceId = dataFields[0].TestInstanceId;
				}

				// Ensuring the correct category names
				this.assessments[i].AssessmentDataField.forEach(field =>  {
					if (!dataCategories[categoryIndex].fields.includes(field.strFieldName)) {
						dataCategories[categoryIndex].fields.push(field.strFieldName);
					}
					if (this.assessments[i].BESTOF) {
						if (!bestOfCategories[bestOfIndex].fields.includes(field.strFieldName)) {
							bestOfCategories[bestOfIndex].fields.push(field.strFieldName);
						}
					}
				});

				// Iterating through the data rows
				this.assessments[i].AssessmentsDataRows.forEach(dataRow => {

					// Creating dictionary for each data row
					const dictionaryData: {[key: string]: string} = {};
					dataRow.AssessmentData.forEach(data => {
						dictionaryData[data.strFieldName] = data.strFieldData;
					});

					// Adding it to our categories if it's not there (preventing duplicates)
					if (!dataCategories[categoryIndex].contains(dictionaryData)) {
						dataCategories[categoryIndex].data.push(dictionaryData);
					}
					if (this.assessments[i].BESTOF) {
						if (!bestOfCategories[bestOfIndex].contains(dictionaryData)) {
							bestOfCategories[bestOfIndex].data.push(dictionaryData);
						}
					}
				});
			}
		}

        // Making data arrays MatTableDataSources
		dataArrays.forEach(dataArray => {

			// Sorting them alphabetically initially
			dataArray.sort((a, b) => {
				if (a.fullTitle < b.fullTitle) {
					return -1;
				} else if (b.fullTitle < a.fullTitle) {
					return 1;
				} else {
					return 0;
				}
			});

			// Sorting each one by date
			dataArray.forEach(dataCategory => {
				dataCategory.sort('Test Date', new Date());
            });

            // Constructing the subtable data
            dataArray.forEach(data => {
                data.createSubTableData();
            });

			// Inserting into table data
			this.tableDataV9.push(new TylerMatTableDataSource(dataArray));
		});
	}

	arrangeTabsV10() {
		// PRE: Your user MUST be in V10, or this doesn't make sense
		// POST: finds the tabs and arranges the data for each tab
		this.tableDataV10 = [];
		this.dataCategoriesV10 = new Array<AssessmentDataCategory>();

		// Creating a data category for each tab
		for (let i = 0; i < this.tabs.length; i++) {
			this.dataCategoriesV10.push(new AssessmentDataCategory());
			this.dataCategoriesV10[this.dataCategoriesV10.length - 1].category = this.tabs[i].DESCRIPTION;
			this.dataCategoriesV10[this.dataCategoriesV10.length - 1].fullTitle = this.tabs[i].FULLTITLE;
		}

		for (let i = 0; i < this.assessments.length; i++) {

			// Determining where to place this
			const categoryIndex = this.tabNames.indexOf(this.assessments[i].DESCRIPTION);
			const dataCategory = this.dataCategoriesV10[categoryIndex];

			// Ensuring the correct field names
			this.assessments[i].AssessmentDataField.forEach(field => {
				if (!dataCategory.fields.includes(field.strFieldName)) {
					dataCategory.fields.push(field.strFieldName);
				}
			});

			// Iterating through data rows
			this.assessments[i].AssessmentsDataRows.forEach(dataRow => {

				// Creating dictionary for each data row
				const dictionaryData: {[key: string]: string} = {};
				let nonEmpty = false;
				dataRow.AssessmentData.forEach(data => {
					if (data.strFieldData != undefined && data.strFieldData != '') {
						dictionaryData[data.strFieldName] = data.strFieldData;
						nonEmpty = true;
					}
				});

				// Adding it to our categories if it's not there (preventing duplicates)
				if (nonEmpty && !dataCategory.contains(dictionaryData)) {
					dataCategory.data.push(dictionaryData);
				}
			});
		}

		// Making each dataCategory a MatDataSource
		this.dataCategoriesV10.forEach(dataCategory => {

			// Sorting them
			dataCategory.sort('Test Year', null, 'asc');

			// Passing in the data to the table
			this.tableDataV10.push(new TylerMatTableDataSource(dataCategory.data));
		});
	}

	tabTrackBy(index: number, item: string) {
	    return item + index;
    }

    regEnrollMode() {
        return this.router.url.toLowerCase().indexOf('registrationenrollment') > -1;
    }
}